import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend, CartesianGrid, Tooltip, LabelList, Cell } from "recharts";
import styles from "./css/BarsGraph.module.css";
import { LoadingWaves } from '../addons/LoadingWaves';
import { formatNumberWithSuffix , formatNumberWithCommas} from "@/utils/numbers";
interface ChargingData {
  מגורים: {
    "סך חניות": number;
    "עמדות טעינה": number;
    "הספק טעינה צפוי": number;
  };
  "שימושים אחרים": {
    "סך חניות": number;
    "עמדות טעינה": number;
    "הספק טעינה צפוי": number;
  };
}

interface ChartDataItem {
  name: string;
  parking: number;
  charging: number;
  power: number;
  parkingLabel: string;
  chargingLabel: string;
  powerLabel: string;
}

const COLORS: { [key: string]: string } = {
  "סך חניות": "#4A90E2",
  "עמדות טעינה": "#50C878",
  "הספק טעינה צפוי": "#FFB347"
};

interface ChargingBarsGraphProps {
  data: ChargingData | any;
  size?: 'small' | 'normal';
}

export const ChargingBarsGraph: React.FC<ChargingBarsGraphProps> = ({ data, size = 'normal' }) => {
  const [barSize, setBarSize] = useState(25);

  const formatRoundedDown = (value: number) => {
  if (typeof value !== 'number') return value;
  return Math.floor(value).toString(); 
};

  const formatNumber = (value: number) => {
    return formatNumberWithSuffix(value);
  };

  // Transform data for the chart
  const transformedData = data ? [
    {
      name: "מגורים",
      parking: data["מגורים"]["סך חניות"],
      charging: data["מגורים"]["עמדות טעינה"],
      power: data["מגורים"]["הספק טעינה צפוי"],
      parkingLabel: `סך חניות: ${formatNumberWithSuffix(data["מגורים"]["סך חניות"])}`,
      chargingLabel: `עמדות טעינה: ${formatNumberWithSuffix(data["מגורים"]["עמדות טעינה"])}`,
      powerLabel: `הספק טעינה צפוי: ${formatNumberWithSuffix(data["מגורים"]["הספק טעינה צפוי"])}`
    },
    {
      name: "שימושים אחרים",
      parking: data["שימושים אחרים"]["סך חניות"],
      charging: data["שימושים אחרים"]["עמדות טעינה"],
      power: data["שימושים אחרים"]["הספק טעינה צפוי"],
      parkingLabel: `סך חניות: ${formatNumberWithSuffix(data["שימושים אחרים"]["סך חניות"])}`,
      chargingLabel: `עמדות טעינה: ${formatNumberWithSuffix(data["שימושים אחרים"]["עמדות טעינה"])}`,
      powerLabel: `הספק טעינה צפוי: ${formatNumberWithSuffix(data["שימושים אחרים"]["הספק טעינה צפוי"])}`
    }
  ] : [];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
      <p className="text-sm font-medium mb-1">{label}</p>
      {payload.map((item: any, index: number) => {
        const key = item.name;
        const value = item.value;
        const isRounded = key === "סך חניות" || key === "עמדות טעינה";
        const displayValue = isRounded ? formatRoundedDown(value) : formatNumberWithCommas(value);

        return (
          <div key={index} className="flex items-center gap-2 text-sm">
            <span 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: item.fill }}
            />
            <span>{item.name}:</span>
            <span className="font-medium">{formatNumberWithCommas(value)}</span>
          </div>
        );
      })}
    </div>
  );
};

  const renderLegend = () => {
    return (
      <ul className="flex flex-wrap justify-center gap-2 mb-2 text-xs">
        {Object.entries(COLORS).map(([type, color]) => (
          <li key={type} className="flex items-center gap-2">
            <span 
              className="inline-block w-2 h-2 rounded-full" 
              style={{ backgroundColor: color }}
            />
            <span className="text-navyBlue">
              {type}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  if (!data) {
    return (
      <div className={styles.container}>
        <LoadingWaves color={Object.values(COLORS)[0]} />
      </div>
    );
  }

  const maxValue = Math.max(...transformedData.map(item => item.parking));
  const displayMaxValue = Math.ceil(maxValue * 1.1); // Add 10% padding

  const margins = size === 'small' 
    ? { top: 40, right: 10, left: -15, bottom: 5 }
    : { top: 50, right: 30, left: -15, bottom: 5 };

  const barCategoryGap = size === 'small' ? 20 : 40;
  const actualBarSize = size === 'small' ? 12 : 15;
  const fontSize = size === 'small' ? 8 : 10;

  return (
    <div className={styles.container } >
      {renderLegend()}
      <div className="h-[70%] 2xl:h-[80%] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={margins}
            barGap={12}
            barCategoryGap={barCategoryGap}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E5E5E5"
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6C7693', fontSize }}
              dy={5}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6C7693', fontSize: fontSize - 1 }}
              tickFormatter={formatNumber}
              domain={[0, displayMaxValue]}
              dx={-5}
            />
            <Tooltip content={CustomTooltip} />
            <Bar dataKey="parking" name="סך חניות" fill={COLORS["סך חניות"]} radius={[3, 3, 0, 0]} barSize={actualBarSize}>
              <LabelList dataKey="parkingLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
            <Bar dataKey="charging" name="עמדות טעינה" fill={COLORS["עמדות טעינה"]} radius={[3, 3, 0, 0]} barSize={actualBarSize}>
              <LabelList dataKey="chargingLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
            <Bar dataKey="power" name="הספק טעינה צפוי" fill={COLORS["הספק טעינה צפוי"]} radius={[3, 3, 0, 0]} barSize={actualBarSize}>
              <LabelList dataKey="powerLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}; 